import { useState, useEffect, useRef, useMemo } from "react";
import debounce from "lodash.debounce";
import { Input } from "../components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command";
import { Popover, PopoverContent, PopoverAnchor } from "../components/ui/popover";

export default function Autocomplete({ 
  options = [], 
  keyname = "option",
  value,
  onChange, 
  onSelect,
  error 
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const inputRef = useRef(null);
  
  const placeholder = `Search ${keyname}s...`;

  // Sync internal state when external value changes,
  // but only when the dropdown is closed (user isn't actively typing)
  useEffect(() => {
    if (!open) {
      setInputValue(value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const filteredOptions = inputValue.trim() === "" 
    ? options
    : options.filter(opt =>
        typeof opt === 'string' && opt.toLowerCase().includes(inputValue.toLowerCase())
      );

  // Keep a stable ref to the onChange prop so useMemo doesn't recreate the debouncer
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const debouncedOnChange = useMemo(
    () => debounce((val) => {
      if (onChangeRef.current) onChangeRef.current(val);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);
  
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedOnChange(newValue);
    if (!open) setOpen(true);
  };
  
  const handleSelect = (val) => {
    setInputValue(val);
    if (onSelect) onSelect(val);
    setOpen(false);
  };
  
  const handleFocus = () => {
    setOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={inputRef}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor>
          <Input
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            className={`w-full ${error ? 'border-red-500' : ''}`}
          />
        </PopoverAnchor>
        <PopoverContent 
          className="w-full p-0" 
          align="start" 
          sideOffset={5}
        >
          <Command>
            <CommandGroup className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <CommandItem
                    key={opt}
                    onSelect={() => handleSelect(opt)}
                    onPointerDown={() => handleSelect(opt)} 
                    className="cursor-pointer"
                  >
                    {opt}
                  </CommandItem>
                ))
              ) : (
                <CommandEmpty className="w-full px-4 text-gray">No {keyname}s found</CommandEmpty>
              )}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}